import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStoreByName } from '@web/hooks/use-stores.hooks';

/**
 * מכסה את הפסוק עד שהזיהוי באמת מקשיב.
 *
 * כל עוד הכיסוי למעלה, שום צליל לא נשלח לזיהוי: לא רעש רקע ולא דיבור של
 * מי שעוד לא התכוון להתחיל. הכיסוי יורד בדיוק כשהשרת מאשר שהזיהוי מקשיב,
 * ובאותו רגע השמע מתחיל לעבור.
 *
 * הפסוק נראה מתחת, מטושטש: רואים שהוא מחכה, אבל אי אפשר להתחיל לקרוא.
 * בפתיחה קרה: טבעת "הֲפֹךְ בָּהּ" מסתובבת, ולקוראים חדשים טיפ אחד בכל פעם.
 * בזרימה רציפה (המיקרופון כבר פתוח) ההמתנה קצרה, ולכן רק כיסוי קל.
 */

const RING_PHRASE = 'הֲפֹךְ בָּהּ וַהֲפֹךְ בָּהּ דְּכֹלָּא בָהּ';
const NBSP3 = '   ';

/** כמה פתיחות קרות מציגים טיפים, לפני שהטבעת תופסת את מקומם לבד */
const TIP_STARTS = 10;
/** המתנה ארוכה מציגה את הטיפ הבא במקום להחזיק אחד */
const TIP_EVERY_MS = 1200;
const TIPS_SEEN_KEY = 'shanenli:start-tips-seen';
const TIPS_OFF_KEY = 'shanenli:start-tips-off';

const readNumber = (key: string): number => {
  try {
    return Number(localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
};
const writeValue = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // אחסון חסום - הטיפים פשוט יופיעו שוב בפעם הבאה
  }
};

/** אות אחת עם הניקוד והטעמים שלה, כדי שהיפוך הסדר לא יפריד ביניהם */
const letterClusters = (text: string): string[] => text.match(/\P{M}\p{M}*/gu) ?? [];
const reverseLetters = (text: string): string => letterClusters(text).reverse().join('');

const TIP_ICONS: Record<string, React.ReactElement> = {
  startTipSitStraight: (
    <svg viewBox="0 0 64 64"><circle cx="30" cy="10" r="6" /><path d="M30 16v22h14v18" /><path d="M30 24l10 6" /><path d="M18 20v36M18 40h12" /></svg>
  ),
  startTipCloseToMic: (
    <svg viewBox="0 0 64 64"><circle cx="16" cy="26" r="10" /><path d="M12 24h1M19 24h1M14 31q2 2 4 0" /><path d="M29 26h8M33 22l4 4-4 4" /><rect x="44" y="14" width="10" height="18" rx="5" /><path d="M40 27a9 9 0 0 0 18 0M49 36v8M44 44h10" /></svg>
  ),
  startTipDeepBreath: (
    <svg viewBox="0 0 64 64"><path d="M10 22h28a7 7 0 1 0-7-7" /><path d="M6 32h44a7 7 0 1 1-7 7" /><path d="M12 42h18a6 6 0 1 1-6 6" /></svg>
  ),
  startTipReadLoud: (
    <svg viewBox="0 0 64 64"><path d="M10 26h8l12-10v32L18 38h-8z" /><path d="M38 24a10 10 0 0 1 0 16M44 18a18 18 0 0 1 0 28M50 12a26 26 0 0 1 0 40" /></svg>
  ),
  startTipReadClearly: (
    <svg viewBox="0 0 64 64">
      <path d="M6 12h52a4 4 0 0 1 4 4v24a4 4 0 0 1-4 4H30l-10 9v-9H6a4 4 0 0 1-4-4V16a4 4 0 0 1 4-4z" />
      <text x="48" y="33" textAnchor="middle" direction="rtl" fontSize="12" fontWeight="700" stroke="none" fill="currentColor">עַל</text>
      <path d="M36 19v18" strokeWidth="2" strokeDasharray="2 3" />
      <text x="19" y="33" textAnchor="middle" direction="rtl" fontSize="12" fontWeight="700" stroke="none" fill="currentColor">לְבָבְךָ</text>
    </svg>
  ),
};
const TIP_KEYS = Object.keys(TIP_ICONS);

const MutedMic: React.FC = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <rect x="24" y="10" width="16" height="28" rx="8" />
    <path d="M16 32a16 16 0 0 0 32 0M32 48v8M24 56h16" />
    <path className="start-cover__slash-gap" d="M12 10l40 44" />
    <path d="M12 10l40 44" />
  </svg>
);

/** טבעת "הֲפֹךְ בָּהּ" עם המקור באותיות קטנות בין שני החצאים */
const Ring: React.FC<{ source: string; sourceIsHebrew: boolean }> = ({ source, sourceIsHebrew }) => {
  // הדפדפן לא מסדר טקסט מימין לשמאל על קו מעוגל, ולכן האותיות מונחות בסדר הפוך
  // לאורך המעגל (עם הניקוד צמוד לכל אות), והקריאה מימין לשמאל יוצאת נכונה.
  const parts = useMemo(() => {
    const logical: Array<{ text: string; small: boolean; hebrew: boolean }> = [];
    for (let round = 0; round < 2; round++) {
      logical.push(
        { text: RING_PHRASE, small: false, hebrew: true },
        { text: NBSP3, small: false, hebrew: false },
        { text: source, small: true, hebrew: sourceIsHebrew },
        { text: NBSP3, small: false, hebrew: false },
      );
    }
    return logical.reverse().map((part) => ({ ...part, text: part.hebrew ? reverseLetters(part.text) : part.text }));
  }, [source, sourceIsHebrew]);

  return (
    <svg className="start-cover__ring" viewBox="0 0 168 168" aria-hidden="true">
      <defs>
        <path id="start-cover-ring-path" d="M 84,84 m -74,0 a 74,74 0 1,1 148,0 a 74,74 0 1,1 -148,0" />
      </defs>
      <text>
        <textPath href="#start-cover-ring-path" startOffset="0" textLength="462" lengthAdjust="spacing">
          {parts.map((part, i) =>
            part.small ? <tspan key={i} className="start-cover__source">{part.text}</tspan> : part.text,
          )}
        </textPath>
      </text>
    </svg>
  );
};

const StartCover: React.FC = () => {
  const { t, i18n } = useTranslation();
  const speech = useStoreByName('speechRecognitionStore') as any;
  const { isInitializing, isStarted, isDone, hasWarmMicrophone } = speech;

  // עד שהקריאה מתחילה (רגע אחרי שהמסך נטען) ובזמן האתחול - הטקסט מכוסה
  const isCovered = !isDone && (isInitializing || !isStarted);
  const isLight = Boolean(hasWarmMicrophone);
  const isEnglish = (i18n.language || '').startsWith('en');

  const [tipsOff, setTipsOff] = useState(() => readNumber(TIPS_OFF_KEY) === 1);
  const [tipIndex, setTipIndex] = useState(() => readNumber(TIPS_SEEN_KEY));
  const [showTips, setShowTips] = useState(false);

  // בכל פתיחה קרה שמכסה את הפסוק: מחליטים אם להציג טיפים, וסופרים
  useEffect(() => {
    if (!isCovered || isLight) return;
    const seen = readNumber(TIPS_SEEN_KEY);
    const tipsNow = !tipsOff && seen < TIP_STARTS;
    setShowTips(tipsNow);
    if (!tipsNow) return;

    writeValue(TIPS_SEEN_KEY, String(seen + 1));
    setTipIndex(seen);
    const timer = setInterval(() => setTipIndex((i) => i + 1), TIP_EVERY_MS);
    return () => clearInterval(timer);
  }, [isCovered, isLight, tipsOff]);

  if (!isCovered) return null;

  const stopTips = () => {
    writeValue(TIPS_OFF_KEY, '1');
    setTipsOff(true);
    setShowTips(false);
  };

  // בזרימה רציפה: ההמתנה קצרה, מספיק כיסוי קל עם מיקרופון מושתק
  if (isLight) {
    return (
      <div className="start-cover start-cover--light" role="status" aria-label={t('startCoverGetReady')}>
        <div className="start-cover__mic start-cover__mic--small"><MutedMic /></div>
      </div>
    );
  }

  const tipKey = TIP_KEYS[tipIndex % TIP_KEYS.length];

  return (
    <div className="start-cover" role="status" aria-label={t('startCoverGetReady')}>
      {showTips ? (
        <div className="start-cover__tip">
          <div key={tipKey} className="start-cover__tip-icon">{TIP_ICONS[tipKey]}</div>
          <span className="start-cover__tip-text">{t(tipKey)}</span>
          <button type="button" className="start-cover__tips-off" onClick={stopTips}>
            {t('startTipsDontShow')}
          </button>
        </div>
      ) : (
        <>
          <div className="start-cover__wheel">
            <Ring source={isEnglish ? '(Avot 5)' : 'אבות ה'} sourceIsHebrew={!isEnglish} />
            <div className="start-cover__mic"><MutedMic /></div>
          </div>
          <span className="start-cover__label">{t('startCoverGetReady')}</span>
        </>
      )}
    </div>
  );
};

export default observer(StartCover);
